/**
 * ImageUploader Component
 * Reusable drag-and-drop image upload component for avatars and logos
 * Supports preview, progress, error handling, and client-side compression
 */

import React, { useCallback, useState, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import imageCompression from 'browser-image-compression';
import { Upload, X, Loader2, ImageIcon, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Compress image before upload
 * Reduces file size and dimensions for faster uploads and lower storage costs
 * Converts to WebP for modern browsers (25-35% smaller files)
 */
async function compressImage(file: File, imageType: 'avatar' | 'logo' = 'logo'): Promise<File> {
  // Skip compression for small files (<200KB) or non-compressible formats
  if (file.size < 200 * 1024 || file.type === 'image/svg+xml') {
    return file;
  }

  // Avatars can be smaller since they're displayed at small sizes
  // Logos need more detail for various display contexts
  const options = {
    maxSizeMB: imageType === 'avatar' ? 1 : 2,
    maxWidthOrHeight: imageType === 'avatar' ? 1024 : 2500,
    useWebWorker: true,
    // Convert to WebP for modern browsers (smaller files, wide support)
    fileType: 'image/webp' as const,
    // Quality settings optimized per type
    initialQuality: imageType === 'avatar' ? 0.85 : 0.90,
  };

  try {
    const compressedFile = await imageCompression(file, options);
    const reduction = ((1 - compressedFile.size / file.size) * 100).toFixed(0);
    console.log(
      `Image compressed: ${(file.size / 1024).toFixed(0)}KB → ${(compressedFile.size / 1024).toFixed(0)}KB (${reduction}% reduction, WebP)`
    );
    return compressedFile;
  } catch (error) {
    console.warn('Image compression failed, using original:', error);
    return file;
  }
}

interface ImageUploaderProps {
  /** Current image URL (if any) */
  currentImage?: string | null;
  /** Callback when upload completes successfully */
  onUpload: (file: File) => Promise<void>;
  /** Callback when image is removed */
  onRemove?: () => Promise<void>;
  /** Accepted MIME types */
  accept?: Record<string, string[]>;
  /** Max file size in bytes (default: 5MB) */
  maxSize?: number;
  /** Shape of the preview */
  shape?: 'circle' | 'square';
  /** Size of the preview */
  size?: 'sm' | 'md' | 'lg' | 'wide' | 'xl';
  /** Label text */
  label?: string;
  /** Help text shown below uploader */
  helpText?: string;
  /** Whether uploader is disabled */
  disabled?: boolean;
  /** Custom class name */
  className?: string;
  /** Image type for compression optimization (avatar = smaller, logo = higher quality) */
  imageType?: 'avatar' | 'logo';
}

const sizeClasses = {
  sm: 'w-16 h-16',
  md: 'w-24 h-24',
  lg: 'w-32 h-32',
  wide: 'w-48 h-24', // Wide format for horizontal logos
  xl: 'w-40 h-40',
};

export function ImageUploader({
  currentImage,
  onUpload,
  onRemove,
  accept = {
    'image/png': ['.png'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/webp': ['.webp'],
    'image/gif': ['.gif'],
  },
  maxSize = 5 * 1024 * 1024, // 5MB
  shape = 'square',
  size = 'lg',
  label,
  helpText = 'PNG, JPG, WebP or GIF. Max 5MB.',
  disabled = false,
  className,
  imageType = 'logo',
}: ImageUploaderProps) {
  const [preview, setPreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const previewRef = useRef<string | null>(null);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;

      const file = acceptedFiles[0];
      setError(null);

      // Create preview from original
      const previewUrl = URL.createObjectURL(file);
      previewRef.current = previewUrl;
      setPreview(previewUrl);

      // Compress and upload
      setIsUploading(true);
      try {
        // Compress image before upload (faster uploads, lower storage costs)
        const compressedFile = await compressImage(file, imageType);
        await onUpload(compressedFile);
        // Keep preview visible until parent updates currentImage
        // The preview will be replaced when currentImage changes
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Upload failed');
        setPreview(null);
        URL.revokeObjectURL(previewUrl);
      } finally {
        setIsUploading(false);
      }
    },
    [onUpload, imageType]
  );

  // Clear preview when currentImage updates (means server returned new URL)
  React.useEffect(() => {
    if (currentImage && preview) {
      URL.revokeObjectURL(preview);
      setPreview(null);
    }
  }, [currentImage]);

  const handleRemove = async () => {
    if (!onRemove) return;
    setIsRemoving(true);
    setError(null);
    try {
      await onRemove();
      setPreview(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove image');
    } finally {
      setIsRemoving(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop,
    accept,
    maxSize,
    multiple: false,
    disabled: disabled || isUploading || isRemoving,
  });

  // Handle file rejections
  React.useEffect(() => {
    if (fileRejections.length > 0) {
      const rejection = fileRejections[0];
      const errorCode = rejection.errors[0]?.code;
      if (errorCode === 'file-too-large') {
        setError(`File too large. Max size is ${Math.round(maxSize / 1024 / 1024)}MB.`);
      } else if (errorCode === 'file-invalid-type') {
        setError('Invalid file type. Please upload an image.');
      } else {
        setError(rejection.errors[0]?.message || 'Invalid file');
      }
    }
  }, [fileRejections, maxSize]);

  const displayImage = preview || currentImage;
  const isLoading = isUploading || isRemoving;

  return (
    <div className={cn('flex flex-col items-center space-y-2', className)}>
      {label && (
        <label className="text-sm font-medium text-foreground">{label}</label>
      )}

      <div
        {...getRootProps()}
        className={cn(
          'relative cursor-pointer border-2 border-dashed transition-colors flex items-center justify-center',
          sizeClasses[size],
          shape === 'circle' ? 'rounded-full' : 'rounded-lg',
          isDragActive
            ? 'border-[#8FB560] bg-[#8FB560]/10'
            : 'border-border hover:border-muted-foreground',
          disabled && 'opacity-50 cursor-not-allowed',
          error && 'border-red-500'
        )}
      >
        <input {...getInputProps()} />

        {/* Image preview or placeholder */}
        {displayImage ? (
          <img
            src={displayImage}
            alt="Preview"
            className={cn(
              'absolute inset-0 w-full h-full',
              // Use object-contain for logos to preserve aspect ratio, object-cover for avatars
              imageType === 'logo' ? 'object-contain p-2' : 'object-cover',
              shape === 'circle' ? 'rounded-full' : 'rounded-lg'
            )}
          />
        ) : (
          <div className="text-muted-foreground">
            {isDragActive ? (
              <Upload className="w-6 h-6 text-[#8FB560]" />
            ) : (
              <ImageIcon className="w-6 h-6" />
            )}
          </div>
        )}

        {/* Loading overlay */}
        {isLoading && (
          <div
            className={cn(
              'absolute inset-0 flex items-center justify-center bg-black/50',
              shape === 'circle' ? 'rounded-full' : 'rounded-lg'
            )}
          >
            <Loader2 className="w-6 h-6 text-foreground animate-spin" />
          </div>
        )}

        {/* Hover overlay (when has image) */}
        {displayImage && !isLoading && (
          <div
            className={cn(
              'absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 hover:opacity-100 transition-opacity',
              shape === 'circle' ? 'rounded-full' : 'rounded-lg'
            )}
          >
            <Upload className="w-6 h-6 text-foreground" />
          </div>
        )}

        {/* Remove button - top right X */}
        {displayImage && onRemove && !isLoading && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleRemove();
            }}
            className={cn(
              'absolute -top-1 -right-1 w-5 h-5 bg-muted hover:bg-red-500 border border-border hover:border-red-500 rounded-full flex items-center justify-center transition-all z-10',
              'focus:outline-none focus:ring-1 focus:ring-muted-foreground'
            )}
            aria-label="Remove image"
          >
            <X className="w-3 h-3 text-muted-foreground hover:text-foreground" />
          </button>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="flex items-center gap-1 text-xs text-red-400">
          <AlertCircle className="w-3 h-3" />
          <span>{error}</span>
        </div>
      )}

      {/* Help text */}
      {helpText && !error && (
        <p className="text-xs text-muted-foreground">{helpText}</p>
      )}
    </div>
  );
}

export default ImageUploader;
